import React, { useState } from "react";
const logo = process.env.PUBLIC_URL + '/images/logo.png';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole, logout } = useAuth();
  const userName = (typeof window !== 'undefined' && localStorage.getItem('user')) || null;
  const makeupArtistName = (typeof window !== 'undefined' && localStorage.getItem('makeupArtistName')) || null;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const active = (path: string) =>
    location.pathname === path
      ? "bg-primary text-white px-4 py-2 rounded-lg font-semibold shadow"
      : "text-white hover:bg-highlight hover:text-secondary px-4 py-2 rounded-lg transition";

  const mobileActive = (path: string) =>
    location.pathname === path
      ? "block bg-primary text-white px-4 py-3 rounded-lg font-semibold shadow"
      : "block text-white hover:bg-highlight hover:text-secondary px-4 py-3 rounded-lg transition";

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
    closeMobileMenu();
  };

  return (
    <nav className="bg-[#B85C1F] text-white shadow rounded-b-2xl border-b border-secondary/10">
      <div className="p-4">
        {/* Top bar with logo and hamburger */}
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}> 
            <img src={logo} alt="Logo" className="h-10 w-10 object-contain rounded-xl shadow" />
            <h1
              className="text-2xl font-semibold tracking-tight text-white"
              style={{
                fontFamily: `'Poppins', 'Montserrat', 'Nunito Sans', Arial, 'sans-serif'`,
                letterSpacing: '-0.5px',
                textTransform: 'none',
                textShadow: '0 1px 8px rgba(78,52,46,0.10)'
              }}
            >
              ArtistDesk
            </h1>
          </div>

          {/* Right side: Login/Logout + Hamburger on mobile, Full menu on desktop */}
          <div className="flex items-center gap-4">
            {/* Login/Logout link - visible on mobile */}
            <div className="lg:hidden">
              {!userRole ? (
                <Link to="/login" className="text-white hover:underline text-sm">
                  Login
                </Link>
              ) : (
                <button onClick={handleLogout} className="text-white hover:underline text-sm">
                  Logout
                </button>
              )}
            </div>

            {/* Desktop Navigation - hidden on mobile */}
            <div className="hidden lg:flex items-center space-x-4">
              {!userRole ? (
                <>
                  <Link to="/about" className={active("/about")}>
                    About Us
                  </Link>
                  <Link to="/contact" className={active("/contact")}>
                    Contact Us
                  </Link>
                  <Link to="/signup" className={active("/signup")}>
                    Signup
                  </Link>
                  <Link to="/login" className={active("/login")}>
                    Login
                  </Link>
                </>
              ) : (
                <>
                  {userRole === "Admin" && (
                    <>
                      {/* Dashboard */}
                      <Link to="/admin/dashboard" className={active("/admin/dashboard")}>
                        Dashboard
                      </Link>

                      {/* Users dropdown */}
                      <div className="relative group">
                        <button className="text-white hover:bg-highlight hover:text-secondary px-4 py-2 rounded-lg transition inline-flex items-center gap-1">
                          Users
                          <span>▾</span>
                        </button>
                        <div className="absolute hidden group-hover:block bg-white text-secondary rounded shadow-lg mt-1 min-w-[200px] z-20">
                          <Link to="/admin/users" className="block px-4 py-2 hover:bg-background">Users</Link>
                          <Link to="/register" className="block px-4 py-2 hover:bg-background">Register</Link>
                          <Link to="/admin/makeup-artists" className="block px-4 py-2 hover:bg-background">Artists</Link>
                          <Link to="/admin/artist-types" className="block px-4 py-2 hover:bg-background">Artist Type</Link>
                        </div>
                      </div>

                      {/* Services */}
                      <Link to="/admin/services" className={active("/admin/services")}>
                        Services
                      </Link>

                      {/* Outsource Roles */}
                      <Link to="/admin/outsource-roles" className={active("/admin/outsource-roles")}>
                        Outsource Roles
                      </Link>

                      {/* Subscription dropdown */}
                      <div className="relative group">
                        <button className="text-white hover:bg-highlight hover:text-secondary px-4 py-2 rounded-lg transition inline-flex items-center gap-1">
                          Subscription
                          <span>▾</span>
                        </button>
                        <div className="absolute hidden group-hover:block bg-white text-secondary rounded shadow-lg mt-1 min-w-[220px] z-20">
                          <Link to="/admin/plans" className="block px-4 py-2 hover:bg-background">Subscription Plans</Link>
                          <Link to="/admin/artist-subscriptions" className="block px-4 py-2 hover:bg-background">Artist Subscriptions</Link>
                        </div>
                      </div>

                      {/* Email dropdown */}
                      <div className="relative group">
                        <button className="text-white hover:bg-highlight hover:text-secondary px-4 py-2 rounded-lg transition inline-flex items-center gap-1">
                          Email
                          <span>▾</span>
                        </button>
                        <div className="absolute hidden group-hover:block bg-white text-secondary rounded shadow-lg mt-1 min-w-[220px] z-20">
                          <Link to="/admin/email-templates" className="block px-4 py-2 hover:bg-background">Email Templates</Link>
                          <Link to="/admin/email-senders" className="block px-4 py-2 hover:bg-background">Email Senders</Link>
                          <Link to="/admin/email-mappings" className="block px-4 py-2 hover:bg-background">Email Mappings</Link>
                        </div>
                      </div>
                    </>
                  )}
                  {userRole === "Artist" && (
                    <>
                      <Link to="/artist/dashboard" className={active("/artist/dashboard")}>
                        Dashboard
                      </Link>
                      <Link to="/artist/bookings" className={active("/artist/bookings")}>
                        Bookings
                      </Link>
                      <Link to="/artist/reports" className={active("/artist/reports")}>
                        Reports
                      </Link>
                      <Link to="/artist/outsource-persons" className={active("/artist/outsource-persons")}>
                        Outsource Persons
                      </Link>
                      <Link to="/artist/service-prices" className={active("/artist/service-prices")}>
                        Service Prices
                      </Link>
                    </>
                  )}
                  {(userName || makeupArtistName) && (
                    <span className="ml-2 text-sm text-pink-100/90">
                      {userName ? `User: ${userName}` : ''}                      
                    </span>
                  )}
                  <button onClick={handleLogout} className="ml-4 text-sm font-medium underline">
                    Logout
                  </button>
                </>
              )}
            </div>

            {/* Hamburger Menu Button - visible on mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden text-white p-2 focus:outline-none"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu - dropdown */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 space-y-2 pb-2">
            {!userRole ? (
              <>
                <Link to="/about" className={mobileActive("/about")} onClick={closeMobileMenu}>
                  About Us
                </Link>
                <Link to="/contact" className={mobileActive("/contact")} onClick={closeMobileMenu}>
                  Contact Us
                </Link>
                <Link to="/signup" className={mobileActive("/signup")} onClick={closeMobileMenu}>
                  Signup
                </Link>
                <Link to="/login" className={mobileActive("/login")} onClick={closeMobileMenu}>
                  Login
                </Link>
              </>
            ) : (
              <>
                {userRole === "Admin" && (
                  <>
                    <Link to="/admin/dashboard" className={mobileActive("/admin/dashboard")} onClick={closeMobileMenu}>
                      Dashboard
                    </Link>

                    {/* Users collapsible */}
                    <details className="px-4" open={false}>
                      <summary className="cursor-pointer py-2">Users</summary>
                      <div className="pl-4 pb-2 space-y-2">
                        <Link to="/admin/users" className={mobileActive("/admin/users")} onClick={closeMobileMenu}>Users</Link>
                        <Link to="/register" className={mobileActive("/register")} onClick={closeMobileMenu}>Register</Link>
                        <Link to="/admin/makeup-artists" className={mobileActive("/admin/makeup-artists")} onClick={closeMobileMenu}>Artists</Link>
                        <Link to="/admin/artist-types" className={mobileActive("/admin/artist-types")} onClick={closeMobileMenu}>Artist Type</Link>
                      </div>
                    </details>

                    {/* Services */}
                    <Link to="/admin/services" className={mobileActive("/admin/services")} onClick={closeMobileMenu}>
                      Services
                    </Link>

                    {/* Outsource Roles */}
                    <Link to="/admin/outsource-roles" className={mobileActive("/admin/outsource-roles")} onClick={closeMobileMenu}>
                      Outsource Roles
                    </Link>

                    {/* Subscription collapsible */}
                    <details className="px-4" open={false}>
                      <summary className="cursor-pointer py-2">Subscription</summary>
                      <div className="pl-4 pb-2 space-y-2">
                        <Link to="/admin/plans" className={mobileActive("/admin/plans")} onClick={closeMobileMenu}>Subscription Plans</Link>
                        <Link to="/admin/artist-subscriptions" className={mobileActive("/admin/artist-subscriptions")} onClick={closeMobileMenu}>Artist Subscriptions</Link>
                      </div>
                    </details>

                    {/* Email collapsible */}
                    <details className="px-4" open={false}>
                      <summary className="cursor-pointer py-2">Email</summary>
                      <div className="pl-4 pb-2 space-y-2">
                        <Link to="/admin/email-templates" className={mobileActive("/admin/email-templates")} onClick={closeMobileMenu}>Email Templates</Link>
                        <Link to="/admin/email-senders" className={mobileActive("/admin/email-senders")} onClick={closeMobileMenu}>Email Senders</Link>
                        <Link to="/admin/email-mappings" className={mobileActive("/admin/email-mappings")} onClick={closeMobileMenu}>Email Mappings</Link>
                      </div>
                    </details>
                  </>
                )}
                {userRole === "Artist" && (
                  <>
                    <Link to="/artist/dashboard" className={mobileActive("/artist/dashboard")} onClick={closeMobileMenu}>
                      Dashboard
                    </Link>
                    <Link to="/artist/bookings" className={mobileActive("/artist/bookings")} onClick={closeMobileMenu}>
                      Bookings
                    </Link>
                    <Link to="/artist/reports" className={mobileActive("/artist/reports")} onClick={closeMobileMenu}>
                      Reports
                    </Link>
                    <Link to="/artist/outsource-persons" className={mobileActive("/artist/outsource-persons")} onClick={closeMobileMenu}>
                      Outsource Persons
                    </Link>
                    <Link to="/artist/service-prices" className={mobileActive("/artist/service-prices")} onClick={closeMobileMenu}>
                      Service Prices
                    </Link>
                  </>
                )}
                {(userName || makeupArtistName) && (
                  <div className="px-4 py-2 text-sm text-pink-100/90">
                    {userName ? `User: ${userName}` : ''}
                    {userName && makeupArtistName ? ' • ' : ''}
                    {makeupArtistName ? `Artist: ${makeupArtistName}` : ''}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

